import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table, Form, Input, Button, DatePicker, Card, Space, message, Tag, Select, Modal,
} from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
dayjs.locale('zh-cn');
import { SearchOutlined, ReloadOutlined, CheckCircleOutlined, QrcodeOutlined } from '@ant-design/icons';
import axios from 'axios';

const API = import.meta.env.VITE_API_BASE || '/api';
const token = () => localStorage.getItem('ar_token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const PAGE_SIZE_OPTIONS = [20, 40, 60, 80, 100];
const SIGN_STATUS_OPTIONS = [
  { label: '未签收', value: 'unsigned' },
  { label: '已签收', value: 'signed' },
];
const SELF_PICKUP_OPTIONS = [
  { label: '全部', value: '' },
  { label: '自提', value: '1' },
  { label: '非自提', value: '0' },
];
const SEAL_DONE_OPTIONS = [
  { label: '已制作', value: '1' },
  { label: '未制作', value: '0' },
];

function formatNum(n) {
  return n != null ? n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0';
}

export default function ArrivalSelfPickup() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({ sign_status: 'unsigned', self_pickup: '1', seal_done: '1' });
  const [qrModal, setQrModal] = useState({ open: false, orderNo: '' });
  const [sortInfo, setSortInfo] = useState({});

  const initialFormValues = useMemo(() => ({ sign_status: 'unsigned', self_pickup: '1', seal_done: '1' }), []);

  // Compute selected total quantity
  const selectedTotalQty = selectedRowKeys.reduce((sum, key) => {
    const row = data.find(r => r.发货单号 === key);
    return sum + (row?.整单合计 || 0);
  }, 0);

  const fetchData = useCallback(async (page = 1, pageSize = pagination.pageSize, sort = {}) => {
    setLoading(true);
    try {
      const params = { ...filters, ...sort, page, page_size: pageSize, grouped: true };
      const r = await axios.get(`${API}/arrival/list`, { headers: headers(), params });
      setData(r.data.data);
      setPagination({ current: page, pageSize, total: r.data.total });
      setSelectedRowKeys([]);
    } catch { message.error('查询失败'); }
    setLoading(false);
  }, [filters, pagination.pageSize]);

  const handleSearch = () => {
    setTimeout(() => {
      const values = form.getFieldsValue();
      const params = {};
      if (values.dateRange && values.dateRange.length === 2) {
        params.start_date = values.dateRange[0].format('YYYY-MM-DD');
        params.end_date = values.dateRange[1].format('YYYY-MM-DD');
      }
      if (values.order_no) params.order_no = values.order_no;
      if (values.customer) params.customer = values.customer;
      const signStatus = (typeof values.sign_status === 'string' && values.sign_status !== '[object Object]') ? values.sign_status : undefined;
      if (signStatus) params.sign_status = signStatus;
      if (values.signDate && values.signDate.length === 2) {
        params.sign_date_start = values.signDate[0].format('YYYY-MM-DD');
        params.sign_date_end = values.signDate[1].format('YYYY-MM-DD');
      }
      if (values.self_pickup) params.self_pickup = values.self_pickup;
      const sealDone = (typeof values.seal_done === 'string' && values.seal_done !== '[object Object]') ? values.seal_done : undefined;
      if (sealDone) params.seal_done = sealDone;
      setFilters(params);
    }, 0);
  };

  useEffect(() => { fetchData(1, pagination.pageSize); }, [filters]);

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({ sign_status: 'unsigned', self_pickup: '1', seal_done: '1' });
    setFilters({ sign_status: 'unsigned', self_pickup: '1', seal_done: '1' });
    setSearchParams({}, { replace: true });
  };

  const handleTableChange = (pag, _filters, sorter) => {
    const sf = sorter?.column ? { sort_field: sorter.field, sort_order: sorter.order } : {};
    setSortInfo(sf);
    fetchData(pag.current, pag.pageSize, sf);
  };

  const handleSignOne = (record) => {
    navigate(`/customer/arrival-sign/sign-doc?order_nos=${encodeURIComponent(record.发货单号)}&from=self-pickup`);
  };

  const handleBatchSign = () => {
    if (selectedRowKeys.length === 0) return;
    navigate(`/customer/arrival-sign/sign-doc?order_nos=${encodeURIComponent(selectedRowKeys.join(','))}&from=self-pickup`);
  };

  const columns = useMemo(() => [
    {
      title: '签收',
      width: 80,
      fixed: 'left',
      render: (_, record) => (
        record.signed ? (
          <Space size={0} direction="vertical" style={{ gap: 2 }}>
            <Tag color={record.sign_type === 'asign' ? 'purple' : 'success'} icon={<CheckCircleOutlined />}>
              {record.sign_type === 'asign' ? '已回签' : '已签'}
            </Tag>
            {record.has_signed_doc && (
              <a onClick={(e) => {
                e.preventDefault();
                navigate(`/customer/arrival-sign/view/${record.发货单号}`);
              }} style={{ fontSize: 11, cursor: 'pointer' }}>📄 查看签收单</a>
            )}
            {record.sign_type === 'asign' && (
              <Button size="small" type="link" style={{ fontSize: 11, padding: 0, height: 18 }}
                onClick={() => {
                  axios.get(`${API}/arrival/evidence/${record.发货单号}`, { headers: headers() })
                    .then(r => {
                      if (r.data.pdf_base64) {
                        const a = document.createElement('a');
                        a.href = 'data:application/pdf;base64,' + r.data.pdf_base64;
                        a.download = `存证_${record.发货单号}.pdf`; a.click();
                      } else message.warning('暂无可下载的存证');
                    }).catch(() => message.error('下载失败'));
                }}>📥 下载存证</Button>
            )}
          </Space>
        ) : (
          <Button size="small" type="primary" ghost disabled={!record.seal_done}
            title={!record.seal_done ? '该客户尚未完成入库章制作' : ''}
            onClick={() => handleSignOne(record)}>
            签收
          </Button>
        )
      ),
    },
    { title: '发货日期', dataIndex: '发货日期', width: 110, render: (v) => v ? v.slice(0, 10) : '', sorter: true },
    { title: '发货单号', dataIndex: '发货单号', width: 200, ellipsis: true },
    { title: '快递单号', dataIndex: '顺丰单号', width: 130, ellipsis: true },
    { title: '仓库', dataIndex: '仓库', width: 100, ellipsis: true,
      render: (v) => v?.includes('上海') ? '上海仓' : '成都仓' },
    { title: '入库章', dataIndex: 'seal_done', width: 90, align: 'center',
      render: (v) => v ? <Tag color="success">已制作</Tag> : <Tag color="default">未制作</Tag> },
    { title: '数量', dataIndex: '整单合计', width: 100, align: 'right', render: (v) => v != null ? v.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '-' },
    { title: '签收备注', dataIndex: 'remark', width: 150, ellipsis: true, render: (v) => v || '-' },
    { title: '签收日期', dataIndex: 'signed_at', width: 110, sorter: true, render: (v) => v || '-' },
    { title: '签收二维码', width: 100,
      render: (_, record) => (
        <Button size="small" icon={<QrcodeOutlined />} type="link"
          onClick={() => setQrModal({ open: true, orderNo: record.发货单号 })}>
          查看
        </Button>
      ),
    },
  ], []);

  const isShanghaiWarehouse = useCallback((wh) => {
    if (!wh) return false;
    return wh.includes('上海');
  }, []);

  const rowSelection = {
    selectedRowKeys,
    getCheckboxProps: (record) => ({ disabled: record.signed || !record.seal_done }),
    onSelectAll: (selected) => {
      if (selected) {
        const candidates = data.filter(r => !r.signed && r.seal_done);
        if (candidates.length === 0) {
          message.warning('请先为客户完成入库章制作');
          return;
        }
        const shanghaiKeys = candidates.filter(r => isShanghaiWarehouse(r.仓库)).map(r => r.发货单号);
        const nonShanghaiKeys = candidates.filter(r => !isShanghaiWarehouse(r.仓库)).map(r => r.发货单号);
        if (shanghaiKeys.length > 0 && nonShanghaiKeys.length > 0) {
          message.warning('上海仓与非上海仓不能同时签收，请重新选择');
          return;
        }
        setSelectedRowKeys(candidates.map(r => r.发货单号));
      } else {
        setSelectedRowKeys([]);
      }
    },
    onSelect: (record, selected) => {
      if (selected) {
        if (!record.seal_done) {
          message.warning('该客户尚未完成入库章制作');
          return;
        }
        const newWarehouse = record.仓库 || '';
        const isNewShanghai = isShanghaiWarehouse(newWarehouse);
        if (selectedRowKeys.length > 0) {
          const existingRow = data.find(r => r.发货单号 === selectedRowKeys[0]);
          const existingWarehouse = existingRow?.仓库 || '';
          const isExistingShanghai = isShanghaiWarehouse(existingWarehouse);
          if (isNewShanghai !== isExistingShanghai) {
            message.warning('上海仓与非上海仓不能同时签收，请重新选择');
            return;
          }
        }
        setSelectedRowKeys(prev => [...new Set([...prev, record.发货单号])]);
      } else {
        setSelectedRowKeys(prev => prev.filter(k => k !== record.发货单号));
      }
    },
  };

  return (
    <Card>
      <Form form={form} layout="inline" initialValues={initialFormValues} style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Form.Item name="dateRange" label="发货日期">
          <DatePicker.RangePicker style={{ width: 240 }} />
        </Form.Item>
        <Form.Item name="order_no" label="发货单号">
          <Input placeholder="发货单号" style={{ width: 160 }} allowClear />
        </Form.Item>
        <Form.Item name="customer" label="客户名称">
          <Input placeholder="客户名称/代码" style={{ width: 180 }} allowClear />
        </Form.Item>
        <Form.Item name="sign_status" label="签收状态">
          <Select style={{ width: 110 }} options={SIGN_STATUS_OPTIONS} allowClear placeholder="全部"
            onChange={(val) => { form.setFieldsValue({ sign_status: val }); handleSearch(); }} />
        </Form.Item>
        <Form.Item name="signDate" label="签收日期">
          <DatePicker.RangePicker style={{ width: 240 }} />
        </Form.Item>
        <Form.Item name="self_pickup" label="是否自提">
          <Select style={{ width: 110 }} options={SELF_PICKUP_OPTIONS} onChange={() => handleSearch()} />
        </Form.Item>
        <Form.Item name="seal_done" label="是否制作入库章">
          <Select style={{ width: 130 }} options={SEAL_DONE_OPTIONS} onChange={() => handleSearch()} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#8c8c8c', fontSize: 13 }}>
          共 <strong>{pagination.total.toLocaleString()}</strong> 条
        </span>
        <Space>
          {selectedRowKeys.length > 0 && (
            <Tag color="blue">本次签收数量合计：{formatNum(selectedTotalQty)}</Tag>
          )}
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleBatchSign}
            disabled={selectedRowKeys.length === 0}
          >
            批量签收 ({selectedRowKeys.length})
          </Button>
        </Space>
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        rowKey="发货单号"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
          showTotal: (t) => `共 ${t} 条`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200, y: 'calc(100vh - 350px)' }}
        size="middle"
      />

      <Modal title={`签收二维码 - ${qrModal.orderNo}`} open={qrModal.open}
        onCancel={() => setQrModal({ open: false, orderNo: '' })}
        footer={null} width={360} centered>
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <img src={`${API}/arrival/qrcode/${qrModal.orderNo}`}
            alt="签收二维码" style={{ width: 220, height: 220 }} />
          <div style={{ marginTop: 12, color: '#666', fontSize: 13 }}>
            发货单号：{qrModal.orderNo}
          </div>
          <div style={{ marginTop: 4, color: '#999', fontSize: 12 }}>
            扫描二维码进入签收页面
          </div>
          <Button type="primary" style={{ marginTop: 12 }}
            onClick={() => {
              const a = document.createElement('a');
              a.href = `${API}/arrival/qrcode/${qrModal.orderNo}`;
              a.download = `签收二维码_${qrModal.orderNo}.png`;
              a.click();
            }}>下载二维码</Button>
        </div>
      </Modal>
    </Card>
  );
}
